import { useNavigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ArrowUpRight, Check } from "lucide-react";

const councilPoints = [
  "Validate incoming submissions in minutes, not days",
  "Apply one consistent acceptance standard",
  "Spot non-compliance and data gaps instantly",
  "Compare resubmissions against the original",
];
const consultantPoints = [
  "Produce ADAC-compliant XML with built-in checks",
  "Pre-validate against the exact Council standard",
  "Profile data quality before you deliver",
  "Cut rejections and avoid costly rework",
];

export default function AudienceSplit() {
  const navigate = useNavigate();
  return (
    <section data-testid="audience-split-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0033A0] font-mono">Built for both sides</p>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-zinc-900 leading-tight">
            The same standard — whether you review submissions or create them.
          </h2>
          <p className="mt-4 text-zinc-600 text-lg">
            We give Consultants exactly what Councils use to validate, and vice versa. No surprises at handover.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-zinc-200 rounded-2xl overflow-hidden">
          <Reveal className="bg-[#121316] text-white p-10 md:p-12">
            <p className="text-xs font-mono tracking-[0.2em] uppercase text-blue-300">Review & validate</p>
            <h3 className="mt-3 font-heading text-2xl font-medium">For Councils</h3>
            <p className="mt-3 text-zinc-400 leading-relaxed">
              Accept only compliant, audit-ready ADAC data — with confidence and a clear paper trail.
            </p>
            <ul className="mt-7 space-y-3">
              {councilPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-200">
                  <Check size={16} className="shrink-0 mt-0.5 text-green-400" /> {p}
                </li>
              ))}
            </ul>
            <button
              data-testid="audience-councils-button"
              onClick={() => navigate("/for-councils")}
              className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-blue-300 transition-colors"
            >
              Explore for Councils <ArrowUpRight size={16} />
            </button>
          </Reveal>

          <Reveal delay={0.1} className="bg-white p-10 md:p-12">
            <p className="text-xs font-mono tracking-[0.2em] uppercase text-[#0033A0]">Create & deliver</p>
            <h3 className="mt-3 font-heading text-2xl font-medium text-zinc-900">For Consultants</h3>
            <p className="mt-3 text-zinc-600 leading-relaxed">
              Deliver right the first time. Build, check, and profile ADAC XML before it leaves your desk.
            </p>
            <ul className="mt-7 space-y-3">
              {consultantPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-700">
                  <Check size={16} className="shrink-0 mt-0.5 text-[#0033A0]" /> {p}
                </li>
              ))}
            </ul>
            <button
              data-testid="audience-consultants-button"
              onClick={() => navigate("/for-consultants")}
              className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-[#0033A0] hover:text-[#00257A] transition-colors"
            >
              Explore for Consultants <ArrowUpRight size={16} />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
